import { Header, Table } from '@tanstack/react-table';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { getPerformerAndVenueForEvent } from 'src/utils/eventWithDataUtils';

import {
  BooleanFilterUI,
  DateRangeFilterUI,
  DualModeNumberFilterUI,
  extractUniqueValues,
  MoneyRangeFilterUI,
  MultiSelectFilterUI,
  NumberRangeFilterUI,
  TextSearchFilterUI,
} from '../filters/FilterComponents';

interface ColumnHeaderFilterProps<T extends Record<string, any>> {
  table: Table<T>;
  header: Header<T, unknown>;
}

export const ColumnHeaderFilter = <T extends Record<string, any>>({
  table,
  header,
}: ColumnHeaderFilterProps<T>) => {
  // Get catalog data for venue lookup (needed for marketplace broadcast)
  const { data: catalogData } = useCatalogDataContext();

  if (!header.column.getCanFilter()) {
    return null;
  }

  const columnFilterVariant = header.column.columnDef.meta?.columnFilterVariant;
  if (!columnFilterVariant) {
    return null;
  }

  const filterValue = header.column.getFilterValue();

  const onChange = (newValue: any) => {
    table.resetRowSelection();
    header.column.setFilterValue(newValue);
  };

  const onClear = () => {
    table.resetRowSelection();
    header.column.setFilterValue(undefined);
  };

  // Simple switch based on columnFilterVariant
  switch (columnFilterVariant) {
    case 'numberRange': {
      return (
        <NumberRangeFilterUI
          value={(filterValue as [number, number]) ?? [undefined, undefined]}
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'multiSelect': {
      const uniqueValueVariant =
        header.column.columnDef.meta?.uniqueValueVariant;

      // Get venue for marketplace broadcast variant
      let venue = null;
      if (uniqueValueVariant === 'marketplaceBroadcast') {
        const firstRow = table
          .getCoreRowModel()
          .flatRows.find((r) => r.original?.listing);
        const event = firstRow?.original?.event;
        if (event && catalogData) {
          const fullInfo = getPerformerAndVenueForEvent(event, catalogData);
          venue = fullInfo.venue;
        }
      }

      const options = extractUniqueValues(
        table.getCoreRowModel().flatRows,
        header.column.id,
        uniqueValueVariant,
        venue
      );

      return (
        <MultiSelectFilterUI
          value={(filterValue as string[]) ?? []}
          onChange={onChange}
          onClear={onClear}
          options={options}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'textSearch': {
      return (
        <TextSearchFilterUI
          value={(filterValue as string) ?? ''}
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'dateRange': {
      return (
        <DateRangeFilterUI
          value={(filterValue as [Date, Date]) ?? [undefined, undefined]}
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'moneyRange': {
      return (
        <MoneyRangeFilterUI
          value={
            (filterValue as {
              currency?: string;
              min?: number;
              max?: number;
            }) ?? {
              currency: undefined,
              min: undefined,
              max: undefined,
            }
          }
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'boolean': {
      return (
        <BooleanFilterUI
          value={(filterValue as boolean | null) ?? null}
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    case 'dualModeNumber': {
      return (
        <DualModeNumberFilterUI
          value={
            (filterValue as {
              mode?: 'absolute' | 'relative';
              min?: number;
              max?: number;
            }) ?? {
              mode: undefined,
              min: undefined,
              max: undefined,
            }
          }
          onChange={onChange}
          onClear={onClear}
          options={{}}
          columnId={header.column.id}
          table={table}
        />
      );
    }

    default:
      return null;
  }
};
